import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { useAuth } from '@/hooks/useAuth';
import { syncNativeRevenueCatAttribution } from '@/services/nativeBilling';
import {
  getNativeAppsFlyerId,
  setNativeAppsFlyerAnonymized,
  setNativeAppsFlyerCustomerUserId,
  setNativeAppsFlyerUserEmail,
} from '@/utils/nativeAppsFlyerAttribution';
import { getNativeTrackingContext } from '@/utils/nativeTrackingContext';
import {
  TRACKING_CONSENT_EVENT,
  getTrackingConsentStatus,
  isAdDataOptedOutOnDevice,
  resolveAdIdentityPermission,
  setTrackingUserId,
  type AdIdentityPermission,
  persistNativeInstallSessionId,
  syncNativeTrackingConsentStatus,
  trackEvent,
  type TrackingConsentStatus,
} from '@/utils/trackingManager';
import {
  getNativeTrackingAuthorizationStatus,
  isNativeTrackingTransparencyRuntime,
  requestNativeTrackingPermission,
  type NativeTrackingAuthorizationStatus,
} from '@/utils/nativeTrackingTransparency';
import { isFirstRunTrackingDeferredPath } from '@/utils/firstRunRoutes';
import {
  registerTrackingConsentRequestHandler,
  requestTrackingConsentOnce,
  type TrackingConsentPromptReason,
} from '@/utils/trackingConsentPrompt';

export { isFirstRunTrackingDeferredPath };

const NATIVE_FIRST_OPEN_TRACKED_KEY = 'jp_native_first_open_tracked';
const APPSFLYER_ID_RETRY_COUNT = 5;
const APPSFLYER_ID_RETRY_DELAY_MS = 600;

type NativeLifecycleEventName = 'native_app_install' | 'native_app_open';
type NativeLifecycleReason = 'bootstrap' | 'resume' | 'tracking_consent_granted';

function mapNativeStatusToConsentStatus(
  status: NativeTrackingAuthorizationStatus
): TrackingConsentStatus {
  switch (status) {
    case 'authorized':
      return 'granted';
    case 'denied':
    case 'restricted':
      return 'denied';
    case 'notDetermined':
    case 'unsupported':
    default:
      return 'pending';
  }
}

function hasTrackedNativeFirstOpen(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(NATIVE_FIRST_OPEN_TRACKED_KEY) === '1';
  } catch {
    return false;
  }
}

function markNativeFirstOpenTracked(): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(NATIVE_FIRST_OPEN_TRACKED_KEY, '1');
  } catch {
    // Ignore localStorage failures.
  }
}

function sendNativeAppsFlyerUserEmail(email: string): void {
  // Never for a member who opted out of ad data sharing on this device or
  // whose account opt-out was applied here.
  if (isAdDataOptedOutOnDevice()) return;
  void setNativeAppsFlyerUserEmail(email).catch((error) => {
    console.error('Error setting AppsFlyer user email:', error);
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function getNativeAppsFlyerIdWithRetry(): Promise<string | null> {
  let lastError: unknown = null;

  for (let attempt = 0; attempt < APPSFLYER_ID_RETRY_COUNT; attempt += 1) {
    try {
      const appsflyerId = await getNativeAppsFlyerId();
      if (appsflyerId) return appsflyerId;
    } catch (error) {
      lastError = error;
    }

    if (attempt < APPSFLYER_ID_RETRY_COUNT - 1) {
      await sleep(APPSFLYER_ID_RETRY_DELAY_MS);
    }
  }

  if (lastError && import.meta.env.DEV) {
    console.warn('Failed to read native AppsFlyer id for lifecycle tracking:', lastError);
  }

  return null;
}

async function trackNativeLifecycleEvent(
  eventName: NativeLifecycleEventName,
  reason: NativeLifecycleReason,
  userId: string | undefined,
  shouldCancel: () => boolean
): Promise<void> {
  const nativeContext = getNativeTrackingContext();
  if (!nativeContext.is_native) return;

  const trackingConsentStatus = getTrackingConsentStatus();
  const [appInfoResult, appsflyerIdResult] = await Promise.allSettled([
    CapacitorApp.getInfo(),
    getNativeAppsFlyerIdWithRetry(),
  ]);
  if (shouldCancel()) return;

  const appInfo = appInfoResult.status === 'fulfilled' ? appInfoResult.value : null;
  const appsflyerId = appsflyerIdResult.status === 'fulfilled' ? appsflyerIdResult.value : null;

  await trackEvent(
    eventName,
    {
      ...nativeContext,
      reason,
      app_version: appInfo?.version || null,
      app_build: appInfo?.build || null,
      app_name: appInfo?.name || null,
      appsflyer_id: appsflyerId || null,
      tracking_consent_status: trackingConsentStatus,
      native_user_agent: window.navigator.userAgent || null,
      measurement_method: eventName === 'native_app_install' ? 'first_open_proxy' : 'app_lifecycle',
    },
    { serverOnly: true, userId }
  );
}

/**
 * The fallback prompt is only for members past first-run onboarding: a real
 * account, or a guest whose onboarding is finished or skipped. A brand-new
 * native guest who opens a shared link (/p/:id, /j/:id) is still first-run
 * and gets the prompt from onboarding's "How it works" screen instead.
 */
export function isPastFirstRunForTracking(
  user: { is_anonymous?: boolean } | null | undefined,
  profile: { onboarding_completed_at?: string | null; onboarding_skipped_at?: string | null } | null | undefined
): boolean {
  if (!user || !profile) return false;
  if (user.is_anonymous !== true) return true;
  return Boolean(profile.onboarding_completed_at || profile.onboarding_skipped_at);
}

export function NativeTrackingBootstrap() {
  const { user, profile } = useAuth();
  const signedInUserIdForRevenueCat = user?.id ?? null;
  const location = useLocation();
  const pathnameRef = React.useRef(location.pathname);
  const pastFirstRun = isPastFirstRunForTracking(user, profile);
  const pastFirstRunRef = React.useRef(pastFirstRun);

  React.useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  React.useEffect(() => {
    pastFirstRunRef.current = pastFirstRun;
  }, [pastFirstRun]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !user?.id) return;

    const nativeContext = getNativeTrackingContext();
    if (!nativeContext.is_native) return;

    const nativePlatform = nativeContext.native_platform;
    if (nativePlatform !== 'android' && nativePlatform !== 'ios') return;

    // Link this user to their AppsFlyer install as early as possible, on auth,
    // independent of RevenueCat billing configuration. Previously the CUID was
    // set ONLY inside the billing-gated attribution sync below, so any user
    // whose RC config failed (module load, offerings error, native billing off)
    // was never linked -- silently breaking install->user->revenue attribution
    // and the data we pass back to the ad platforms. This direct call is a no-op
    // off-native and idempotent, so it is safe to run alongside the RC sync.
    // Ad data policy: the AppsFlyer link and email wait for the member's
    // account opt-out read (about 2s at most). Opted out (GPC, an explicit
    // opt-out on this device, or the account) or unreadable: no link this
    // session. An ATT decline alone is not an opt-out.
    const userId = user.id;
    const userEmail = user.email;
    void resolveAdIdentityPermission(userId)
      .then((permission) => {
        if (permission !== 'allowed') return;
        void setNativeAppsFlyerCustomerUserId(userId).catch((error) => {
          console.error('Error setting AppsFlyer customer user id:', error);
        });
        // iOS checks the ATT answer in the native plugin. Android has no OS
        // prompt, so the app's own tracking choice decides here; a later
        // opt-in sends the email from the consent listener below.
        if (userEmail && (nativePlatform === 'ios' || getTrackingConsentStatus() === 'granted')) {
          sendNativeAppsFlyerUserEmail(userEmail);
        }
      })
      .catch((error) => {
        console.error('Error resolving the ad data policy:', error);
      });

    if (nativePlatform === 'android') {
      setTrackingUserId(user.id, user.email);
    }

    void syncNativeRevenueCatAttribution(user.id, { force: true }).catch((error) => {
      console.error(`Error syncing ${nativePlatform} RevenueCat attribution identifiers:`, error);
    });
  }, [user?.email, user?.id]);

  React.useEffect(() => {
    if (!CapacitorApp || typeof window === 'undefined') return;

    let cancelled = false;

    const bootstrap = async () => {
      if (!hasTrackedNativeFirstOpen()) {
        // Store the install session key BEFORE the event fires so the durable
        // key always equals the session id the install event row carries.
        persistNativeInstallSessionId();
        await trackNativeLifecycleEvent('native_app_install', 'bootstrap', user?.id, () => cancelled);
        markNativeFirstOpenTracked();
      }

      await trackNativeLifecycleEvent('native_app_open', 'bootstrap', user?.id, () => cancelled);
    };

    void bootstrap();

    const listenerPromise = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      void trackNativeLifecycleEvent('native_app_open', 'resume', user?.id, () => cancelled);
    });

    return () => {
      cancelled = true;
      void listenerPromise.then((listener) => listener.remove());
    };
  }, [user?.id]);

  // Both platforms: a consent change (Your Privacy Choices, the Android
  // toggle) that leaves this member opted out of ad data sharing clears
  // RevenueCat's $appsflyerId and other ad identifiers on the device at once
  // (syncRevenueCatAttributionIdentifiers takes the clear path), even
  // offline; the server clear follows when the account write lands.
  React.useEffect(() => {
    if (typeof window === 'undefined' || !signedInUserIdForRevenueCat) return;
    const nativeContext = getNativeTrackingContext();
    if (!nativeContext.is_native) return;
    const userId = signedInUserIdForRevenueCat;
    let cancelled = false;
    const handleConsentChange = () => {
      void resolveAdIdentityPermission(userId)
        .then((permission) => {
          if (cancelled || permission !== 'opted_out') return;
          return syncNativeRevenueCatAttribution(userId, { force: true });
        })
        .catch((error) => {
          console.error('Error clearing RevenueCat ad identifiers after an opt-out:', error);
        });
    };
    window.addEventListener(TRACKING_CONSENT_EVENT, handleConsentChange);
    return () => {
      cancelled = true;
      window.removeEventListener(TRACKING_CONSENT_EVENT, handleConsentChange);
    };
  }, [signedInUserIdForRevenueCat]);

  // Android: carry the app's tracking choice into the native AppsFlyer SDK.
  // An opt-out anonymizes it; an opt-in restores it and sends the email hash.
  // For a signed-in member the SDK is restored only when the ad data policy
  // allows their identity (resolveAdIdentityPermission === 'allowed'); while
  // it is 'unknown' or 'opted_out' the SDK stays anonymized.
  const userEmailRef = React.useRef(user?.email ?? null);
  React.useEffect(() => {
    userEmailRef.current = user?.email ?? null;
  }, [user?.email]);
  const signedInUserId = user?.id ?? null;

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const nativeContext = getNativeTrackingContext();
    if (!nativeContext.is_native || nativeContext.native_platform !== 'android') return;

    let cancelled = false;
    const applyAndroidAppsFlyerConsent = async (sendEmailOnGrant: boolean) => {
      const status = getTrackingConsentStatus();
      if (status !== 'granted' && status !== 'denied') return;

      let anonymize = status === 'denied';
      if (!anonymize && signedInUserId) {
        const permission = await resolveAdIdentityPermission(signedInUserId).catch(
          (): AdIdentityPermission => 'unknown'
        );
        if (cancelled) return;
        anonymize = permission !== 'allowed';
      }

      void setNativeAppsFlyerAnonymized(anonymize).catch((error) => {
        console.error('Error updating AppsFlyer anonymization:', error);
      });

      const email = userEmailRef.current;
      if (!anonymize && sendEmailOnGrant && email) {
        sendNativeAppsFlyerUserEmail(email);
      }
    };

    // Apply on every launch (and sign-in) so the SDK always matches the stored
    // choice. The login effect above already sends the email on launch.
    void applyAndroidAppsFlyerConsent(false);
    const handleConsentChange = () => {
      void applyAndroidAppsFlyerConsent(true);
    };
    window.addEventListener(TRACKING_CONSENT_EVENT, handleConsentChange);

    return () => {
      cancelled = true;
      window.removeEventListener(TRACKING_CONSENT_EVENT, handleConsentChange);
    };
  }, [signedInUserId]);

  React.useEffect(() => {
    if (!isNativeTrackingTransparencyRuntime()) return;

    let cancelled = false;

    const syncConsentFromDevice = async (
      reason: 'sync' | 'request' | 'resume',
      promptReason?: TrackingConsentPromptReason
    ): Promise<NativeTrackingAuthorizationStatus | null> => {
      const nativeStatus =
        reason === 'request'
          ? await requestNativeTrackingPermission()
          : await getNativeTrackingAuthorizationStatus();
      if (cancelled) return null;

      const consentStatus = mapNativeStatusToConsentStatus(nativeStatus);
      const changed = syncNativeTrackingConsentStatus(consentStatus);

      if (consentStatus === 'granted' && user?.id) {
        setTrackingUserId(user.id, user.email);
        // A later Allow (the fallback prompt or Settings) makes the email
        // hash shareable now; the login-time call was refused natively. It
        // still waits for the ad data policy to allow this member's identity.
        if (user.email && (changed || reason === 'request')) {
          const userId = user.id;
          const email = user.email;
          void resolveAdIdentityPermission(userId)
            .then((permission) => {
              if (!cancelled && permission === 'allowed') sendNativeAppsFlyerUserEmail(email);
            })
            .catch(() => undefined);
        }
        void syncNativeRevenueCatAttribution(user.id, { force: true }).catch((error) => {
          console.error('Error syncing RevenueCat attribution identifiers:', error);
        });
        await trackNativeLifecycleEvent(
          'native_app_open',
          'tracking_consent_granted',
          user.id,
          () => cancelled
        );
      }

      if (changed || reason === 'request') {
        await trackEvent(
          'app_tracking_consent_updated',
          {
            status: consentStatus,
            native_status: nativeStatus,
            reason,
            ...(promptReason ? { prompt_reason: promptReason } : {}),
            platform: 'ios',
          },
          { serverOnly: true, userId: user?.id }
        );
      }

      return nativeStatus;
    };

    // trackingConsentPrompt owns the gate (pending, once per session); this
    // handler owns the side effects that need the signed-in member.
    const unregister = registerTrackingConsentRequestHandler((promptReason) =>
      syncConsentFromDevice('request', promptReason)
    );

    // Fallback prompt for members outside first-run onboarding (returning
    // members, legacy onboarding, sign-ins to an existing account). New
    // members get it from onboarding v3's "How it works" screen. Read the
    // route at request time: a resume can land on any screen.
    const requestFallbackIfAllowed = async () => {
      if (!pastFirstRunRef.current) return;
      if (isFirstRunTrackingDeferredPath(pathnameRef.current)) return;
      await requestTrackingConsentOnce('fallback');
    };

    const bootstrap = async () => {
      await syncConsentFromDevice('sync');
      if (cancelled) return;
      await requestFallbackIfAllowed();
    };

    void bootstrap();

    const listenerPromise = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      void (async () => {
        await syncConsentFromDevice('resume');
        if (cancelled) return;
        await requestFallbackIfAllowed();
      })();
    });

    return () => {
      cancelled = true;
      unregister();
      void listenerPromise.then((listener) => listener.remove());
    };
  }, [user?.id]);

  // Leaving first-run routes (for example "/" redirecting to Home, or a
  // sign-in landing on Home) is the moment the fallback becomes allowed. The
  // mount itself is covered by the bootstrap above, after its first sync.
  // Finishing onboarding (or the profile loading for a returning member) is
  // the same moment, so it re-runs on either change.
  const isDeferredRoute = isFirstRunTrackingDeferredPath(location.pathname);
  const fallbackAllowed = pastFirstRun && !isDeferredRoute;
  const hasSeenRouteRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasSeenRouteRef.current) {
      hasSeenRouteRef.current = true;
      return;
    }
    if (!fallbackAllowed || !isNativeTrackingTransparencyRuntime()) return;
    void requestTrackingConsentOnce('fallback');
  }, [fallbackAllowed]);

  return null;
}

export default NativeTrackingBootstrap;
